<template>
  <div
    class="tw-relative"
    :class="{
      'tw-flex tw-items-center tw-justify-between': !!label
    }"
  >
    <div v-if="label" class="tw-text-xs">
      {{ label }}
    </div>
    <button
      ref="colorPickerButton"
      class="tw-ml-4 tw-flex tw-h-7 tw-w-[130px] tw-items-center tw-overflow-hidden tw-rounded-md tw-border tw-text-2xs tw-leading-5 dark:tw-bg-neutral-900"
      :class="{
        'tw-w-[180px]': type === 'gradient',
        'tw-cursor-pointer focus-within:tw-border-deep-orange-500': !disabled
      }"
      :disabled="disabled"
      @click="openColorPicker"
    >
      <input
        type="text"
        class="tw-h-7 tw-w-[80px] tw-flex-1 tw-border-none tw-font-mono tw-text-xs tw-text-black focus:tw-border-transparent focus:tw-ring-transparent dark:tw-text-white"
        :data-testid="label.replaceAll(' ', '-').toLowerCase()"
        :class="{
          'tw-w-[130px]': type === 'gradient',
          'tw-cursor-pointer': !disabled,
          'tw-cursor-not-allowed': disabled
        }"
        :value="textInputValue"
        readonly
      />
      <div v-if="isTransparent" class="transparent-bg tw-h-7 tw-w-9" />
      <div
        v-else-if="type === 'color'"
        class="tw-h-7 tw-w-9 tw-border-l"
        :style="{ 'background-color': color.style }"
      />
      <div v-else class="tw-h-7 tw-w-9" :style="{ 'background-image': gradient.style }" />
      <div
        v-if="showColorPicker"
        ref="colorPicker"
        v-studio-click-outside="() => (showColorPicker = false)"
        :class="{
          'tw-fixed tw-z-50 tw-bg-white tw-p-0': useFixedPositioning,
          'tw-absolute tw-right-0 tw-top-8 tw-z-50 tw-bg-white tw-p-0': !useFixedPositioning
        }"
        :style="useFixedPositioning ? colorPickerPosition : {}"
      >
        <ColorPicker
          v-if="type === 'color'"
          :color="color"
          :onStartChange="onColorChange"
          :onChange="onColorChange"
          :onEndChange="onColorChange"
        />
        <ColorPicker
          v-else
          isGradient
          :gradient="gradient"
          :onStartChange="onGradientChange"
          :onChange="onGradientChange"
          :onEndChange="onGradientChange"
        />
      </div>
    </button>
  </div>
</template>

<script>
import { ColorPicker } from '@goldcast/color-gradient-picker';
import throttle from 'lodash.throttle';
import { defineComponent } from 'vue';

import { rgbaToHex, destructureRgbColor } from '../utils/color';

const exampleLinearGradient = {
  type: 'linear',
  style: 'linear-gradient(270deg, rgba(117, 117, 255, 1) 0%, rgba(0, 59, 128, 1) 100%)',
  degree: 270,
  points: [
    {
      left: 0,
      red: 117,
      green: 117,
      blue: 255,
      alpha: 1,
      style: 'rgba(117, 117, 255, 1)'
    },
    {
      left: 100,
      red: 59,
      green: 0,
      blue: 0,
      alpha: 1,
      style: 'rgba(59, 0, 0, 1)'
    }
  ]
};

export default defineComponent({
  name: 'StudioColorPickerInput',
  components: {
    ColorPicker
  },
  props: {
    initialColor: { type: String, default: 'rgba(0, 0, 0, 1)' },
    initialGradient: { type: Object, default: () => exampleLinearGradient },
    label: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    /**
     * Type of the color picker
     * @default 'color'
     * @type {'color' | 'gradient'}
     */
    type: { type: String, default: 'color' },
    useFixedPositioning: { type: Boolean, default: false }
  },
  data() {
    return {
      showColorPicker: false,
      color: destructureRgbColor(this.initialColor),
      gradient: this.initialGradient,
      colorPickerPosition: {
        top: '0px',
        left: '0px'
      },
      scrollableParent: null
    };
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);

    if (this.useFixedPositioning) {
      window.removeEventListener('resize', this.calculateColorPickerPosition);
      if (this.scrollableParent) {
        this.scrollableParent.removeEventListener('scroll', this.calculateColorPickerPosition);
      }
    }
  },
  computed: {
    textInputValue() {
      if (this.type === 'gradient' && this.gradient.type === 'linear') {
        return 'Linear Gradient';
      }

      if (this.type === 'gradient' && this.gradient.type === 'radial') {
        return 'Radial Gradient';
      }

      return rgbaToHex(this.color.style, true);
    },
    isTransparent() {
      if (!this.color) {
        return false;
      }

      return this.color.alpha === 0;
    }
  },
  watch: {
    initialColor() {
      this.color = destructureRgbColor(this.initialColor);
    },
    initialGradient() {
      this.gradient = this.initialGradient;
    },
    showColorPicker() {
      if (this.showColorPicker) {
        document.addEventListener('click', this.handleOutsideClick);

        if (this.useFixedPositioning) {
          this.scrollableParent = this.findScrollableParent();
          if (this.scrollableParent) {
            this.scrollableParent.addEventListener('scroll', this.calculateColorPickerPosition);
          }
        }
      } else {
        document.removeEventListener('click', this.handleOutsideClick);

        if (this.useFixedPositioning && this.scrollableParent) {
          this.scrollableParent.removeEventListener('scroll', this.calculateColorPickerPosition);
          this.scrollableParent = null;
        }
      }
    }
  },
  methods: {
    onColorChange: throttle(function (color) {
      this.color = color;
      this.$emit('colorChange', {
        ...this.color,
        hex: rgbaToHex(this.color.style, true)
      });
    }, 200),
    onGradientChange: throttle(function (gradient) {
      this.gradient = gradient;
      this.gradient.style = gradient.style.endsWith(')') ? gradient.style : `${gradient.style})`;
      this.$emit('gradientChange', { ...this.gradient });
    }, 200),
    openColorPicker() {
      if (this.disabled) {
        return;
      }
      this.showColorPicker = true;

      if (this.useFixedPositioning) {
        this.$nextTick(() => {
          this.calculateColorPickerPosition();
        });
      }
    },
    calculateColorPickerPosition() {
      const button = this.$refs.colorPickerButton;
      const colorPicker = this.$refs.colorPicker;

      if (!button || !colorPicker) {
        return;
      }

      const buttonRect = button.getBoundingClientRect();
      const colorPickerRect = colorPicker.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = buttonRect.bottom + 4;
      let left = buttonRect.right - colorPickerRect.width;

      if (left < 0) {
        left = buttonRect.left;
      }

      if (top + colorPickerRect.height > viewportHeight) {
        top = buttonRect.top - colorPickerRect.height - 4;
      }

      const minMargin = 16;
      left = Math.max(minMargin, Math.min(left, viewportWidth - colorPickerRect.width - minMargin));
      top = Math.max(minMargin, top);

      this.colorPickerPosition = {
        top: `${top}px`,
        left: `${left}px`
      };
    },
    findScrollableParent() {
      let element = this.$refs.colorPickerButton;

      while (element && element !== document.body) {
        const styles = window.getComputedStyle(element);
        const overflow = styles.getPropertyValue('overflow');
        const overflowY = styles.getPropertyValue('overflow-y');
        const overflowX = styles.getPropertyValue('overflow-x');

        if (
          overflow === 'auto' ||
          overflow === 'scroll' ||
          overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowX === 'auto' ||
          overflowX === 'scroll'
        ) {
          return element;
        }

        element = element.parentElement;
      }

      return null;
    },
    handleOutsideClick(event) {
      if (!this.showColorPicker) {
        return;
      }

      if (this.$refs.colorPickerButton?.contains(event.target)) {
        return;
      }

      this.showColorPicker = false;
    }
  }
});
</script>

<style lang="scss" scoped>
.transparent-bg {
  background: url('../../tailwind/alpha-background.svg');
}

.ui-color-picker {
  border: 1px solid #bbbfc6;
  margin: 0;
  padding-top: 16px;
}
</style>
